import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';

import { userManager, httpClient, useAppAuth } from '../../logic/imports';

export const TOS_VERSION = 1.0;
export const TOS_CONTENT = `# Terms of Service & Risk Disclaimer

**Effective Date:** 12/5/2025  
**Version:** 1.0

---

## 1. Agreement to Terms
By accessing and using this trading application (the "Application"), you agree to be bound by these Terms of Service. If you do not agree to these terms, you may not use the Application.

---

## 2. RISK DISCLAIMER - PLEASE READ CAREFULLY

### 2.1 Real Money Trading Risk
This Application is designed for **real money trading**. You acknowledge that:
- **You can lose all or substantially all of your invested capital**
- Trading in financial markets involves significant risk
- Past performance does not guarantee future results
- Market conditions can change rapidly and unexpectedly

### 2.2 No Guarantees
The Application makes no representations or warranties regarding:
- The accuracy or completeness of market data
- The profitability or success of any trading strategy
- The performance of any financial instrument
- Future market movements or price levels

### 2.3 Beta/Testing Status
This Application is currently in **beta/testing phase** and may contain:
- Bugs or errors that could result in financial loss
- Incomplete features or unexpected behavior
- Data inconsistencies or calculation errors
- Connectivity issues or service interruptions

You assume all risk associated with using an application under active development.

---

## 3. No Financial Advice

This Application provides tools for trading only. **We do not provide financial, investment, or trading advice.**
- All trading decisions are yours alone
- Do not rely on this Application or its creators for investment guidance
- Consult with a qualified financial advisor before making trading decisions
- You are solely responsible for evaluating the risks and rewards of any trade

---

## 4. User Responsibility

You acknowledge and agree that:
- You are responsible for all trading decisions made through this Application
- You understand the risks associated with trading financial instruments
- You have sufficient knowledge and experience to use this Application responsibly
- You will not use this Application for any illegal or unauthorized purpose
- You will comply with all applicable laws and regulations in your jurisdiction

---

## 5. Limitation of Liability

**TO THE MAXIMUM EXTENT PERMITTED BY LAW:**

The Application and its creator is not liable for:
- Any financial losses or damages from trading activities
- Errors, bugs, or malfunctions in the Application
- Data loss or service interruptions
- Delays or failures in executing trades
- Inaccurate market data or price information
- Any indirect, incidental, special, consequential, or punitive damages

Even if advised of the possibility of such damages.

### 5.1 Liability Cap
In no event shall the creator's total liability exceed the amount you have paid to use this Application in the 12 months preceding the claim, or $100, whichever is less.

---

## 6. Trade Execution on Your Behalf

This Application executes trades on your behalf through the Public.com API based on trading instructions and strategies you configure within the Application. You acknowledge that:
- You authorize the Application to execute trades according to your settings
- All trades executed are based on parameters and decisions you have made
- You are responsible for all trading activity generated through your configured settings
- The Application is not a licensed broker, dealer, or investment advisor
- Any losses resulting from trades executed through your configured settings are your responsibility

---

## 7. Indemnification

You agree to indemnify, defend, and hold harmless the Application's creator from any claims, damages, losses, or expenses (including legal fees) arising from:
- Your use of the Application
- Your trading activities or decisions
- Your violation of these Terms of Service
- Your violation of any applicable law or regulation
- Any normal and expected trading losses

---

## 8. No Warranty

**THE APPLICATION IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND**, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
- Warranties of merchantability
- Fitness for a particular purpose
- Non-infringement of intellectual property rights
- Accuracy or completeness of content

---

## 9. Prohibited Uses

You agree NOT to:
- Use the Application for illegal purposes
- Attempt to manipulate markets or engage in fraud
- Reverse-engineer or attempt to access source code
- Use the Application to test automated attacks or security exploits
- Share your login credentials with others
- Use the Application on behalf of others without authorization

---

## 10. Data Collection & Privacy

### 10.1 What We Collect
For normal operation, the Application will collect:
- Your email address and IP address for registration and authentication
- Your Public.com brokerage "secret" and account ID (used only to authenticate with Public's API)
- Your portfolio data such as account balance, positions, active orders and order history
- Logs relating to your use of the Application during beta testing (to improve functionality and find bugs)

### 10.2 What We Do NOT Collect
**At no time will the Application ever receive, store, or have access to:**
- Your Public.com login ID or password
- Your personal identifying information (name, email, phone, address, SSN, etc.)
- Your bank account or payment information
- Your funds or ability to withdraw money

### 10.3 Public.com API Limitations
The Public.com API connection used by this Application:
- **Does NOT grant access to your funds** - the Application can view portfolio info and execute trades only
- Cannot withdraw, transfer, or move money from your account
- Cannot access account settings or personal information
- Can only execute trades and view trading-related data

All security and fund access controls remain under Public.com's control and your account settings.

---

## 11. Modifications to Terms

We reserve the right to modify these Terms of Service at any time. Material changes will require your re-acceptance before continued use of the Application.

---

## 12. Termination

We reserve the right to terminate your access to the Application at any time, for any reason, with or without notice.

---

## 13. Governing Law

These Terms of Service are governed by the laws of California, United States, without regard to its conflict of law provisions.

---

## 14. Dispute Resolution

Any disputes arising from these Terms of Service shall be resolved through:
1. Good-faith negotiation between parties
2. [Arbitration / Mediation / Court proceedings] as a final resolution

---

## 15. Acknowledgment & Acceptance

**By clicking "I Agree" below, you confirm that:**

- You have read and understood these Terms of Service
- You understand the risks of trading with real money
- You acknowledge this Application is in beta/testing phase
- You accept full responsibility for your trading decisions
- You agree to be bound by these Terms of Service
- You have the legal authority to enter into this agreement

---

## 16. Contact

For questions about these Terms of Service, please contact: david.durston@gmail.com

---

**REMEMBER: Only trade money you can afford to lose.**`;

export const TosPage: React.FC = () => {
	const navigate = useNavigate();
	const { logout } = useAppAuth();
	const contentRef = useRef<HTMLDivElement>(null);
	const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
	const [hasAccepted, setHasAccepted] = useState(false);

	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const element = e.currentTarget;
		const isAtBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 5;
		setHasScrolledToBottom(isAtBottom);
	};

	const handleAccept = async () => {
		try {
			// Send ToS acceptance to backend
			await httpClient.tosAccept(TOS_VERSION);

			// Load user data and preferences after ToS acceptance
			await userManager.load();

			navigate('/dashboard');
		} catch (err) {
			console.error('[TosPage] ToS acceptance failed:', err);
		}
	};

	const handleDecline = async () => {
		console.log('[TosPage] User declined ToS, logging out.');
		// Log out from Auth0
		await logout();
		// Clear any pending auth state
		await userManager.disconnect();
		// Redirect to login with implicit message
		navigate('/');
	};

	return (
		<div className="page">
			<div className="panel panel--clear" style={{ width: '80%', maxWidth: '700px', borderWidth: 0 }}>
				<h1 style={{ textAlign: 'center' }}>Terms of Service</h1>
				
				{/* ToS Content */}
				<div
					ref={contentRef}
					onScroll={handleScroll}
					style={{
						maxHeight: '500px',
						overflowY: 'auto',
						padding: '20px',
						backgroundColor: '#0a0a0a',
						borderRadius: '4px',
						margin: '20px',
						fontSize: '14px',
						lineHeight: '1.6',
						border: '1px solid #ffbb008c'
					}}
				>
					<div style={{ textAlign: 'left' }}>
						<ReactMarkdown>
							{TOS_CONTENT}
						</ReactMarkdown>
					</div>
				</div>

				{/* Acceptance Controls */}
				<div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column' }}>
					<div style={{ position: 'relative', minHeight: '56px' }}>
						<label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: hasScrolledToBottom ? '#ddd' : '#888' }}>
							<input
								type="checkbox"
								checked={hasAccepted}
								onChange={(e) => setHasAccepted(e.target.checked)}
								disabled={!hasScrolledToBottom}
								style={{ cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed' }}
							/>
							I have read and agree to the Terms of Service
						</label>
						<span style={{ 
							position: 'absolute',
							top: '28px',
							left: 0,
							fontSize: '12px', 
							color: '#999',
							visibility: hasScrolledToBottom ? 'hidden' : 'visible'
						}}>
							Please scroll to the bottom to enable
						</span>
					</div>
					
					<div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
						<button
							className="button button--secondary"
							onClick={handleDecline}
							style={{ padding: '10px 20px', fontSize: '14px' }}
						>
							Decline
						</button>
						<button
							className="button button--green"
							onClick={handleAccept}
							disabled={!hasAccepted}
							style={{
								padding: '10px 20px',
								fontSize: '14px',
								opacity: hasAccepted ? 1 : 0.5,
								cursor: hasAccepted ? 'pointer' : 'not-allowed'
							}}
						>
							Accept
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};